<script>
/*
MIT License

Copyright (c) 2017 Luc

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
import katex from 'katex';
import 'katex/contrib/mhchem';
import DOMPurify from 'dompurify';
import {h} from 'vue';
import {useI18n} from 'vue-i18n';
import {generateAccessibleMathLabel} from './mathmlAccessibility';

export default {
  name: 'KatexElement',
  setup() {
    const {t} = useI18n({
      inheritLocale: true,
    });
    return {t};
  },
  props: {
    expression: {
      type: String,
      default: '',
      required: true,
    },
    displayMode: {
      type: Boolean,
      default: false,
    },
    throwOnError: {
      type: Boolean,
      default: false,
    },
    errorColor: {
      type: String,
      default: '#cc0000',
    },
    colorIsTextColor: {
      type: Boolean,
      default: false,
    },
    maxSize: {
      type: Number,
      default: Infinity,
    },
    maxExpand: {
      type: Number,
      default: 1000,
    },
    allowedProtocols: {
      type: Array,
      default: () => ['http', 'https', 'mailto', '_relative'],
    },
    strict: {
      type: [Boolean, String, Function],
      default: 'warn',
    },
  },
  computed: {
    options() {
      return {
        displayMode: this.displayMode,
        throwOnError: this.throwOnError,
        errorColor: this.errorColor,
        colorIsTextColor: this.colorIsTextColor,
        maxSize: this.maxSize,
        maxExpand: this.maxExpand,
        allowedProtocols: this.allowedProtocols,
        strict: this.strict,
      };
    },
    math() {
      return katex.renderToString(this.expression, this.options);
    },
    translate() {
      return typeof this.t === 'function' ? this.t : (key) => key;
    },
    mathmlText() {
      return generateAccessibleMathLabel(this.expression, {
        renderMathMl: (latex) => katex.renderToString(latex, this.options),
        translate: this.translate,
      });
    },
  },
  render() {
    const element = this.displayMode ? 'div' : 'span';
    const hasAccessibleText = !!this.mathmlText;

    const children = [];

    if (hasAccessibleText) {
      children.push(h('span', {class: 'sr-only'}, this.mathmlText));
    }

    children.push(
      h('span', {
        innerHTML: DOMPurify.sanitize(this.math),
        'aria-hidden': 'true',
      })
    );

    return h(element, {}, children);
  },
};
</script>
<style>
@import 'katex/dist/katex.min.css';
</style>
